import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const TagSelector = ({ selectedTags = [], onChange }) => {
  const { token } = useAuth();
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchTags();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTags = async () => {
    try {
      const response = await fetch('/api/tags', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setAllTags(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTag = (tag) => {
    const isSelected = selectedTags.some(t => t.id === tag.id);
    if (isSelected) {
      onChange(selectedTags.filter(t => t.id !== tag.id));
    } else {
      onChange([...selectedTags, tag]);
    }
  };

  const handleRemoveTag = (tagId) => {
    onChange(selectedTags.filter(t => t.id !== tagId));
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    
    setCreating(true);
    try {
      const response = await fetch('/api/tags', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newTagName.trim() })
      });
      
      const data = await response.json();
      if (data.success) {
        // Add to allTags if it's new
        const existingTag = allTags.find(t => t.id === data.data.id);
        if (!existingTag) {
          setAllTags([...allTags, { ...data.data, recipe_count: 0 }]);
        }
        // Add to selected tags
        if (!selectedTags.some(t => t.id === data.data.id)) {
          onChange([...selectedTags, data.data]);
        }
        setNewTagName('');
      }
    } catch (error) {
      console.error('Failed to create tag:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateTag();
    }
  };

  // Filter out already selected tags from dropdown
  const availableTags = allTags.filter(tag => 
    !selectedTags.some(selected => selected.id === tag.id)
  );

  return (
    <div style={styles.container}>
      {/* Selected tags */}
      <div style={styles.selectedTags}>
        {selectedTags.map(tag => (
          <span key={tag.id} style={styles.tag}>
            {tag.name}
            <button 
              type="button"
              onClick={() => handleRemoveTag(tag.id)} 
              style={styles.removeTagBtn}
            >
              ×
            </button>
          </span>
        ))}
        <button 
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          style={styles.addTagBtn}
        >
          + Add Tag
        </button>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div ref={dropdownRef} style={styles.dropdown}>
          {/* Create new tag */}
          <div style={styles.createTag}>
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="New tag name..."
              style={styles.newTagInput}
              maxLength={50}
            />
            <button 
              type="button"
              onClick={handleCreateTag}
              disabled={creating || !newTagName.trim()}
              style={styles.createBtn}
            >
              {creating ? '...' : 'Create'}
            </button>
          </div>

          {/* Existing tags */}
          <div style={styles.tagList}>
            {loading ? (
              <div style={styles.loading}>Loading tags...</div>
            ) : availableTags.length === 0 ? (
              <div style={styles.empty}>No more tags available</div>
            ) : (
              availableTags.map(tag => (
                <div 
                  key={tag.id} 
                  onClick={() => handleToggleTag(tag)}
                  style={styles.tagOption}
                >
                  <span>{tag.name}</span>
                  <span style={styles.tagCount}>{tag.recipe_count || 0} recipes</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    position: 'relative'
  },
  selectedTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    alignItems: 'center',
    minHeight: '40px',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    backgroundColor: '#f9fafb'
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    padding: '0.25rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.875rem',
    fontWeight: '500'
  },
  removeTagBtn: {
    background: 'none',
    border: 'none',
    color: '#1e40af',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0 0.25rem',
    lineHeight: 1
  },
  addTagBtn: {
    backgroundColor: 'transparent',
    border: '1px dashed #9ca3af',
    color: '#6b7280',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '0.25rem',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    zIndex: 50,
    maxHeight: '250px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  createTag: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.75rem',
    borderBottom: '1px solid #e5e7eb'
  },
  newTagInput: {
    flex: 1,
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.875rem'
  },
  createBtn: {
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500'
  },
  tagList: {
    overflowY: 'auto',
    maxHeight: '180px'
  },
  tagOption: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    cursor: 'pointer',
    transition: 'background-color 0.15s'
  },
  tagCount: {
    fontSize: '0.75rem',
    color: '#9ca3af'
  },
  loading: {
    padding: '1rem',
    textAlign: 'center',
    color: '#6b7280'
  },
  empty: {
    padding: '1rem',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '0.875rem'
  }
};

// Add hover effect via JS
const originalTagOptionStyle = styles.tagOption;
styles.tagOption = {
  ...originalTagOptionStyle,
  ':hover': {
    backgroundColor: '#f3f4f6'
  }
};

export default TagSelector;
